import { useCallback, useEffect, useState } from "react";

export interface SelectionInfo {
  text: string;
  side: "source" | "target";
  /** char offset в полном тексте панели (если он передан) */
  offsetStart: number | null;
  offsetEnd: number | null;
  /** ~50 символов до выделения для recovery */
  contextBefore: string | null;
  contextAfter: string | null;
  /** linkId ближайшего terminology-span'а если выделение попало в него */
  nearestLinkId: string | null;
  /** rect для позиционирования popover'а */
  rect: DOMRect;
}

const CONTEXT_LEN = 50;

/**
 * Подписывается на selectionchange. Если выделение находится внутри ref
 * с data-mt-pane="source|target" — возвращает SelectionInfo. Иначе null.
 *
 * `paneText` — полный текст панели, в котором ищем offset выделения. Если
 * не задан — offset'ы будут null (например, в textarea-режиме селект мы
 * берём напрямую через selectionStart/End).
 */
export function useTextSelection(
  paneSelector: string,
  paneTextBySide: Record<"source" | "target", string | null>,
): SelectionInfo | null {
  const [selection, setSelection] = useState<SelectionInfo | null>(null);

  const compute = useCallback((): SelectionInfo | null => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    const text = sel.toString();
    if (!text || !text.trim()) return null;

    const startNode = range.startContainer;
    const startEl =
      startNode.nodeType === Node.ELEMENT_NODE
        ? (startNode as Element)
        : startNode.parentElement;
    if (!startEl) return null;

    const pane = startEl.closest(paneSelector) as HTMLElement | null;
    if (!pane) return null;
    const sideAttr = pane.getAttribute("data-mt-pane-side");
    const side: "source" | "target" =
      sideAttr === "source" ? "source" : "target";

    // Validate selection не вылезает за pane
    if (!pane.contains(range.endContainer)) return null;

    // nearestLinkId
    const closestSpan =
      startEl.closest("[data-mt-link]") as HTMLElement | null;
    const endEl =
      range.endContainer.nodeType === Node.ELEMENT_NODE
        ? (range.endContainer as Element)
        : range.endContainer.parentElement;
    const endLink =
      endEl?.closest("[data-mt-link]")?.getAttribute("data-mt-link") ?? null;
    const startLink = closestSpan?.getAttribute("data-mt-link") ?? null;
    const nearestLinkId = startLink && startLink === endLink ? startLink : null;

    // offset'ы — ищем substring в полном тексте панели. Если множ.
    // вхождений — берём ближайшее к селекту по позиции.
    const fullText = paneTextBySide[side] ?? "";
    let offsetStart: number | null = null;
    let offsetEnd: number | null = null;
    if (fullText && text) {
      const allOccurrences: number[] = [];
      let cur = 0;
      while (cur < fullText.length) {
        const idx = fullText.indexOf(text, cur);
        if (idx < 0) break;
        allOccurrences.push(idx);
        cur = idx + 1;
      }
      if (allOccurrences.length === 1) {
        offsetStart = allOccurrences[0];
        offsetEnd = offsetStart + text.length;
      } else if (allOccurrences.length > 1) {
        // Несколько вхождений — берём первое (для popover это ок,
        // подсветка в editor использует rect, не offset).
        offsetStart = allOccurrences[0];
        offsetEnd = offsetStart + text.length;
      }
    }
    let contextBefore: string | null = null;
    let contextAfter: string | null = null;
    if (offsetStart !== null && offsetEnd !== null && fullText) {
      contextBefore = fullText
        .slice(Math.max(0, offsetStart - CONTEXT_LEN), offsetStart)
        .trim() || null;
      contextAfter = fullText
        .slice(offsetEnd, Math.min(fullText.length, offsetEnd + CONTEXT_LEN))
        .trim() || null;
    }

    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return null;
    return {
      text,
      side,
      offsetStart,
      offsetEnd,
      contextBefore,
      contextAfter,
      nearestLinkId,
      rect,
    };
  }, [paneSelector, paneTextBySide]);

  useEffect(() => {
    let raf = 0;
    const handler = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = compute();
        setSelection(next);
      });
    };
    document.addEventListener("selectionchange", handler);
    return () => {
      document.removeEventListener("selectionchange", handler);
      cancelAnimationFrame(raf);
    };
  }, [compute]);

  return selection;
}

/**
 * Вспомогательная функция: вычистить выделение программно (например после
 * закрытия popover'а или отправки feedback'а).
 */
export function clearTextSelection(): void {
  const sel = window.getSelection();
  if (sel && sel.removeAllRanges) sel.removeAllRanges();
}
